import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Check, Crown, Star, Zap, Shield, Hexagon, Flame } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MembershipPage() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [membershipStatus, setMembershipStatus] = useState(null);

  useEffect(() => {
    loadMembershipData();
  }, []);

  const loadMembershipData = async () => {
    try {
      const [packagesRes, statusRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/memberships/packages`),
        axios.get(`${BACKEND_URL}/api/memberships/status`, { withCredentials: true })
      ]);
      
      setPackages(packagesRes.data);
      setMembershipStatus(statusRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Load membership error:', error);
      setLoading(false);
    }
  };

  const handleSubscribe = async (tier) => {
    setProcessing(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/memberships/subscribe`,
        {
          tier: tier,
          duration_years: 1,
          origin_url: window.location.origin
        },
        { withCredentials: true }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Subscribe error:', error);
      toast.error(error.response?.data?.detail || 'Subscription failed');
      setProcessing(false);
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'free': return <Shield className="w-8 h-8 text-[#B45309]" />; // Bronze
      case 'premium': return <Star className="w-8 h-8 text-[#EAB308]" />; // Gold
      case 'pro': return <Crown className="w-8 h-8 text-[#22D3EE]" />; // Platinum
      default: return <Shield className="w-8 h-8 text-neutral-500" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <Loader2 className="w-12 h-12 text-lime-500 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-sans selection:bg-lime-500/30 overflow-x-hidden relative">
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px', pointerEvents: 'none' }}></div>
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 py-16 relative z-10">
        <div className="text-center mb-16 relative">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-lime-500/10 blur-[100px] rounded-full pointer-events-none"></div>
          <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tight text-white mb-6 relative z-10" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            Level Up Your Trust
          </h1>
          <p className="text-lg text-neutral-400 max-w-2xl mx-auto font-medium relative z-10">
            Unlock premium features and larger rotation capacities to maximize your collective saving power.
          </p>
          
          {membershipStatus && membershipStatus.tier !== 'free' && membershipStatus.is_active && (
            <div className="mt-8 inline-flex items-center px-6 py-3 bg-[#111111] border border-lime-500/30 rounded-full shadow-[0_0_20px_rgba(132,204,22,0.1)] relative z-10">
              <Hexagon className="w-5 h-5 text-lime-400 mr-3" />
              <span className="text-lime-400 font-bold uppercase tracking-wider text-sm">
                Active Tier: {membershipStatus.tier}
              </span>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-20">
          
          {/* Free Tier (Bronze Visuals) */}
          <Card 
            data-testid="free-membership-card"
            className="border-[#B45309]/30 bg-[#111111] relative overflow-hidden group hover:-translate-y-2 transition-all duration-300 rounded-3xl"
          >
            <div className="absolute inset-0 bg-gradient-to-b from-[#B45309]/10 to-transparent opacity-50 pointer-events-none"></div>
            <CardHeader className="pb-6 relative z-10 border-b border-white/5">
              <div className="flex items-center justify-between mb-4">
                <CardTitle className="text-2xl font-black uppercase tracking-tight text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Free</CardTitle>
                {getTierIcon('free')}
              </div>
              <CardDescription className="text-neutral-400 font-medium">Bronze Trust Level Start</CardDescription>
            </CardHeader>
            <CardContent className="pt-8 relative z-10 flex flex-col h-[calc(100%-120px)]">
              <div className="mb-8">
                <div className="text-5xl font-black text-white mb-2 tracking-tight" style={{ fontFamily: 'Unbounded, monospace' }}>
                  $0<span className="text-xl text-neutral-500 font-bold ml-1">/mo</span>
                </div>
                <div className="text-sm text-[#B45309] font-bold uppercase tracking-widest">
                  Forever Free
                </div>
              </div>

              <ul className="space-y-4 mb-8 flex-grow">
                {packages?.free?.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <Check className="w-5 h-5 text-[#B45309] mr-3 flex-shrink-0" />
                    <span className="text-neutral-300 font-medium">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                data-testid="current-plan-free"
                disabled={true}
                variant="outline"
                className="w-full rounded-full py-6 border-white/10 bg-white/5 text-neutral-400 font-bold opacity-50"
              >
                {membershipStatus?.tier === 'free' ? 'Current Tier' : 'Free Forever'}
              </Button>
            </CardContent>
          </Card>

          {/* Premium Tier (Gold Visuals) */}
          <Card 
            data-testid="premium-membership-card"
            className="border-[#EAB308]/50 bg-[#111111] relative overflow-hidden group hover:-translate-y-2 transition-all duration-300 rounded-3xl shadow-[0_0_40px_rgba(234,179,8,0.1)] lg:scale-105 z-10"
          >
            <div className="absolute inset-0 bg-gradient-to-b from-[#EAB308]/20 to-transparent opacity-50 pointer-events-none"></div>
            <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-[#EAB308] to-transparent"></div>
            
            <div className="absolute top-6 right-6">
              <Badge className="bg-[#EAB308] text-black font-black uppercase tracking-wider text-[10px] px-3 py-1 hover:bg-[#EAB308]">Popular</Badge>
            </div>
            
            <CardHeader className="pb-6 relative z-10 border-b border-white/5">
              <div className="flex items-center justify-between mb-4">
                <CardTitle className="text-2xl font-black uppercase tracking-tight text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Premium</CardTitle>
                {getTierIcon('premium')}
              </div>
              <CardDescription className="text-neutral-400 font-medium">Gold Trust Level Access</CardDescription>
            </CardHeader>
            <CardContent className="pt-8 relative z-10 flex flex-col h-[calc(100%-120px)]">
              <div className="mb-8">
                <div className="text-5xl font-black text-white mb-2 tracking-tight" style={{ fontFamily: 'Unbounded, monospace' }}>
                  $5<span className="text-xl text-neutral-500 font-bold ml-1">/mo</span>
                </div>
                <div className="text-sm text-[#EAB308] font-bold uppercase tracking-widest flex items-center">
                  <Flame className="w-4 h-4 mr-1" />
                  Up to 5 rotations
                </div>
              </div>

              <ul className="space-y-4 mb-8 flex-grow">
                {packages?.premium?.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <Check className="w-5 h-5 text-[#EAB308] mr-3 flex-shrink-0" />
                    <span className="text-neutral-200 font-medium">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                data-testid="subscribe-premium"
                onClick={() => handleSubscribe('premium')}
                disabled={processing || membershipStatus?.tier === 'premium'}
                className="w-full rounded-full py-6 bg-[#EAB308] hover:bg-[#FDE047] text-black font-black transition-all"
              >
                {processing ? (
                  <>
                    <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : membershipStatus?.tier === 'premium' ? (
                  'Current Tier'
                ) : (
                  <>
                    <Star className="mr-2 w-5 h-5" />
                    Subscribe - $5/mo
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Pro Tier (Platinum Visuals) */}
          <Card 
            data-testid="pro-membership-card"
            className="border-[#22D3EE]/40 bg-[#111111] relative overflow-hidden group hover:-translate-y-2 transition-all duration-300 rounded-3xl"
          >
            <div className="absolute inset-0 bg-gradient-to-b from-[#22D3EE]/15 to-transparent opacity-50 pointer-events-none"></div>
            
            <div className="absolute top-6 right-6">
              <Badge className="bg-[#22D3EE]/20 text-[#22D3EE] border border-[#22D3EE]/50 font-black uppercase tracking-wider text-[10px] px-3 py-1 hover:bg-[#22D3EE]/30">Elite</Badge>
            </div>
            
            <CardHeader className="pb-6 relative z-10 border-b border-white/5">
              <div className="flex items-center justify-between mb-4">
                <CardTitle className="text-2xl font-black uppercase tracking-tight text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Pro</CardTitle>
                {getTierIcon('pro')}
              </div>
              <CardDescription className="text-neutral-400 font-medium">Platinum Trust Level Power</CardDescription>
            </CardHeader>
            <CardContent className="pt-8 relative z-10 flex flex-col h-[calc(100%-120px)]">
              <div className="mb-8">
                <div className="text-5xl font-black text-white mb-2 tracking-tight" style={{ fontFamily: 'Unbounded, monospace' }}>
                  $20<span className="text-xl text-neutral-500 font-bold ml-1">/mo</span>
                </div>
                <div className="text-sm text-[#22D3EE] font-bold uppercase tracking-widest flex items-center">
                  <Zap className="w-4 h-4 mr-1" />
                  Up to 10 rotations
                </div>
              </div>

              <ul className="space-y-4 mb-8 flex-grow">
                {packages?.pro?.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <Check className="w-5 h-5 text-[#22D3EE] mr-3 flex-shrink-0" />
                    <span className="text-neutral-300 font-medium">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                data-testid="subscribe-pro"
                onClick={() => handleSubscribe('pro')}
                disabled={processing || membershipStatus?.tier === 'pro'}
                className="w-full rounded-full py-6 bg-transparent border-2 border-[#22D3EE] text-[#22D3EE] hover:bg-[#22D3EE] hover:text-black font-black transition-all"
              >
                {processing ? (
                  <>
                    <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : membershipStatus?.tier === 'pro' ? (
                  'Current Tier'
                ) : (
                  <>
                    <Zap className="mr-2 w-5 h-5" />
                    Subscribe - $20/mo
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* FAQ Section */}
        <Card className="border-white/10 bg-[#111111] max-w-4xl mx-auto rounded-3xl overflow-hidden relative">
          <div className="absolute top-0 right-0 w-64 h-64 bg-lime-500/5 blur-[80px] pointer-events-none"></div>
          <CardHeader className="border-b border-white/5 bg-[#1A1A1A] p-8">
            <CardTitle className="text-2xl font-black uppercase tracking-tight text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Arena Rules & FAQ</CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            <div className="grid md:grid-cols-2 gap-10">
              <div>
                <h4 className="font-bold text-white mb-3 flex items-center text-lg">
                  <Hexagon className="w-4 h-4 mr-2 text-lime-400" />
                  How does billing work?
                </h4>
                <p className="text-neutral-400 text-sm leading-relaxed font-medium">
                  You're charged monthly upon entry. You can exit the subscription at any time from your settings.
                </p>
              </div>
              <div>
                <h4 className="font-bold text-white mb-3 flex items-center text-lg">
                  <Hexagon className="w-4 h-4 mr-2 text-lime-400" />
                  Can I upgrade my tier?
                </h4>
                <p className="text-neutral-400 text-sm leading-relaxed font-medium">
                  Yes! You can climb from Premium to Pro at any time, and we'll automatically adjust your active billing cycle.
                </p>
              </div>
              <div>
                <h4 className="font-bold text-white mb-3 flex items-center text-lg">
                  <Hexagon className="w-4 h-4 mr-2 text-lime-400" />
                  What happens if I step down?
                </h4>
                <p className="text-neutral-400 text-sm leading-relaxed font-medium">
                  Your account reverts to the Bronze (Free) tier at the end of the billing period. All your current funds remain secure.
                </p>
              </div>
              <div>
                <h4 className="font-bold text-white mb-3 flex items-center text-lg">
                  <Hexagon className="w-4 h-4 mr-2 text-lime-400" />
                  What are the capacity limits?
                </h4>
                <p className="text-neutral-400 text-sm leading-relaxed font-medium">
                  Bronze: 1 circle. Gold: 5 circles. Platinum: 10 circles. Choose the level that matches your rotation strategy.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

 
